import { useState, useEffect } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useMutation } from 'react-query';
import toast from 'react-hot-toast';
import axios from 'api/request';
import { endPoints } from 'api/endpoints';
import { draftTeams as recoilStoreDraftTeams, teamValidationCache, sspSubmitOrder } from 'pages/provisioning/day2/github-onboarding-new/store';
import { isValidName } from 'pages/provisioning/day2/github-onboarding-new/utils/formValidation';
import { filterRemovedMembers, getGithubOrgUnit } from 'pages/provisioning/day2/github-onboarding-new/utils';

const useTeamExistenceValidation = ({ teamName, index, shouldValidate = true, members = [] }) => {
    const order = useRecoilValue(sspSubmitOrder);
    const [draftTeams, setDraftTeams] = useRecoilState(recoilStoreDraftTeams);
    const [validationCache, setValidationCache] = useRecoilState(teamValidationCache);
    const [teamExistsInOrg, setTeamExistsInOrg] = useState(draftTeams[index]?.teamExistsInOrg || false);
    const [teamExists, setTeamExists] = useState(members.length > 0 ? 'yes' : '');

    const validateTeamApi = useMutation(({ teamName: tn, organizationUnit }) =>
        axios.get(endPoints.day2Operation.GithubOnboarding.validateTeam({ teamName: tn, organizationUnit }))
    );

    useEffect(() => {
        let cancelled = false;

        const checkExistingMembers = async () => {
            if (!shouldValidate || !teamName || teamName.trim() === '' || !isValidName(teamName)) {
                if (!cancelled) {
                    setTeamExistsInOrg(false);
                }
                return;
            }

            // If we've already validated this team name in this session, use cached result
            if (validationCache[teamName]) {
                if (cancelled) return;

                const cached = validationCache[teamName];
                const teamExistsFlag = cached?.teamExists;
                const apiMembers = cached?.members || [];
                const removedMembersList = draftTeams?.[index]?.removedMembers || [];
                const filtered = filterRemovedMembers(apiMembers, removedMembersList);
                const teamExistsWithMembers = teamExistsFlag && filtered.length > 0;

                setTeamExistsInOrg(!!teamExistsFlag);

                if (teamExistsFlag && members && members.length > 0) {
                    setTeamExists('yes');
                }

                setDraftTeams((prevTeams) =>
                    prevTeams.map((team, i) =>
                        i === index
                            ? { ...team, hasExistingMembers: teamExistsWithMembers, existingOrgMembers: filtered, teamExistsInOrg: !!teamExistsFlag }
                            : team
                    )
                );
                return;
            }

            try {
                const response = await validateTeamApi.mutateAsync({
                    teamName,
                    organizationUnit: getGithubOrgUnit(order?.environment),
                });

                if (cancelled) return;

                const responseData = response?.data;
                const teamExistsFlag = responseData?.teamExists;
                const apiMembers = responseData?.members;
                const removedMembersList = draftTeams?.[index]?.removedMembers || [];
                const filtered = filterRemovedMembers(apiMembers || [], removedMembersList);
                const teamExistsWithMembers = teamExistsFlag && filtered.length > 0;

                setTeamExistsInOrg(!!teamExistsFlag);

                if (teamExistsFlag && members && members.length > 0) {
                    setTeamExists('yes');
                }

                // Cache successful validation to avoid future API calls for same name within this order
                setValidationCache((prev) => ({ ...prev, [teamName]: { teamExists: !!teamExistsFlag, members: apiMembers || [] } }));

                setDraftTeams((prevTeams) =>
                    prevTeams.map((team, i) =>
                        i === index
                            ? {
                                  ...team,
                                  hasExistingMembers: teamExistsWithMembers,
                                  existingOrgMembers: filtered,
                                  teamExistsInOrg: !!teamExistsFlag,
                              }
                            : team
                    )
                );
            } catch (err) {
                if (!cancelled) {
                    // eslint-disable-next-line no-console
                    console.error('Error checking existing members:', err);
                    toast.error('Team validation failed. Please try again or contact support.');
                    setTeamExistsInOrg(false);
                    setDraftTeams((prevTeams) =>
                        prevTeams.map((team, i) =>
                            i === index ? { ...team, hasExistingMembers: false, existingOrgMembers: [], teamExistsInOrg: false } : team
                        )
                    );
                }
            }
        };

        checkExistingMembers();

        return () => {
            cancelled = true;
        };
    }, [teamName, shouldValidate, index]);

    return {
        teamExistsInOrg,
        isValidating: validateTeamApi.isLoading,
        teamExists,
        setTeamExists,
    };
};

export default useTeamExistenceValidation;
